// Leveringsstatus for utgående GNÅ-meldinger — den rene delen.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERRAPPORT 2026-08-18: «Det ser ut som man snakker sammen, men jeg får ikke
// meldingene.» Rotårsaken lå i hubbens utboks (bølge 209, se
// `services/hub/src/utboks.ts`), men den ANDRE halvparten av problemet var her:
// Feltveksleren kunne bare vise ÉN ting — «Køet for GNÅ» — fordi det var alt
// hubben noen gang svarte. Operatøren hadde ingen måte å se om meldingen faktisk
// nådde fram.
//
// ⚠ «KØET» OG «LEVERT» SKAL ALDRI SE LIKE UT. Det var nettopp fordi de gjorde det
// at feilen kunne stå i dagevis: en kø som ikke ble tømt var visuelt identisk med
// et samband som virket.
//
// Hubben har nå `GET /samband/status?id=`, og denne modulen inneholder de tre
// tingene som kan regnes feil: statustolkningen, teksten operatøren leser, og
// pollingtakten.

/** Statusen en utgående melding kan ha. */
export type SambandStatus =
  /** POST-en til hubben er ikke besvart ennå. */
  | 'sender'
  /** Hubben har tatt imot, men broen har ikke hentet den. */
  | 'koet'
  /** Broen har hentet den og forsøker å levere. */
  | 'utlevert'
  /** Broen har bekreftet at den er sendt inn i GNÅ. */
  | 'levert'
  /** Hubben ga opp etter for mange forsøk. ⚠ Har alltid en grunn. */
  | 'oppgitt'
  /** POST-en til hubben feilet — meldingen forlot aldri COP. */
  | 'lokal-feil'
  /** Hubben kjenner ikke id-en (omstart før journalskriving, eller feil id). */
  | 'ukjent';

/**
 * Er statusen endelig?
 *
 * ⚠ BARE `levert`, `oppgitt` OG `lokal-feil` ER ENDELIGE. `ukjent` er det IKKE:
 * hubben kan ha startet på nytt midt i, og da er svaret «vi vet ikke ennå»
 * framfor «meldingen finnes ikke». Å slutte å spørre på `ukjent` ville låst
 * meldingen i en tilstand som ser ut som en dom.
 */
export function erEndelig(s: SambandStatus): boolean {
  return s === 'levert' || s === 'oppgitt' || s === 'lokal-feil';
}

export interface KvitteringVisning {
  tekst: string;
  /** CSS-klasse: `ok` (levert), `pending` (underveis), `fail` (feilet). */
  klasse: 'ok' | 'pending' | 'fail';
  /** Fyldig forklaring til `title` — grunnen når det finnes en. */
  detalj: string;
}

/**
 * Tid før en melding som står i kø skal begynne å bekymre.
 *
 * Broen henter normalt hvert par sekund. Står en melding i kø i to minutter, er
 * broen sannsynligvis nede — og da skal operatøren FÅ VITE DET, i stedet for å
 * lese «Køet» som «på vei».
 */
export const KOE_BEKYMRING_MS = 120_000;

/**
 * Teksten operatøren leser under sin egen melding.
 *
 * ⚠ «LEVERT» BETYR «SENDT INN I GNÅ», IKKE «LEST». Vi vet at broen fikk den inn
 * i rommet; vi vet ikke om mottakeren har åpnet appen. Teksten sier derfor
 * «Levert til GNÅ», ikke «Levert» alene — forskjellen er hele poenget når
 * spørsmålet er «har hun sett det?».
 */
export function kvitteringVisning(
  status: SambandStatus,
  opts: { grunn?: string; koetMs?: number; naa?: number; forsok?: number } = {},
): KvitteringVisning {
  const naa = opts.naa ?? Date.now();
  const grunn = (opts.grunn ?? '').trim();
  switch (status) {
    case 'sender':
      return { tekst: 'Sender…', klasse: 'pending', detalj: 'Meldingen er på vei til hubben.' };
    case 'levert':
      return {
        tekst: '✓ Levert til GNÅ', klasse: 'ok',
        detalj: 'Broen har bekreftet at meldingen er sendt inn i GNÅ. Dette betyr IKKE '
          + 'at mottakeren har lest den.',
      };
    case 'oppgitt':
      return {
        tekst: `✗ IKKE LEVERT${grunn ? `: ${grunn}` : ''}`, klasse: 'fail',
        detalj: grunn
          ? `Hubben ga opp etter flere forsøk. Grunn fra broen: ${grunn}`
          : 'Hubben ga opp etter flere forsøk, uten at broen oppgav en grunn.',
      };
    case 'lokal-feil':
      return {
        tekst: '✗ Nådde ikke hubben', klasse: 'fail',
        detalj: 'Meldingen forlot aldri denne maskinen. Sjekk nettet mot hubben og send på nytt.',
      };
    case 'utlevert': {
      const t = opts.forsok && opts.forsok > 1 ? ` (forsøk ${opts.forsok})` : '';
      return {
        tekst: `Leverer${t}…`, klasse: 'pending',
        detalj: grunn
          ? `Broen forsøker igjen. Forrige feil: ${grunn}`
          : 'Broen har hentet meldingen og sender den inn i GNÅ.',
      };
    }
    case 'ukjent':
      return {
        tekst: '? Ukjent status', klasse: 'pending',
        detalj: 'Hubben kjenner ikke denne meldingen. Ble hubben startet på nytt? '
          + 'Send på nytt hvis den ikke dukker opp.',
      };
    case 'koet':
    default: {
      const alder = opts.koetMs ? naa - opts.koetMs : 0;
      if (alder > KOE_BEKYMRING_MS) {
        const min = Math.round(alder / 60_000);
        return {
          // ⚠ ETTER TO MINUTTER ER «KØET» EN ADVARSEL, IKKE EN STATUS. En kø som
          // ikke tømmes er visuelt identisk med et samband som virker — og det
          // var nettopp den likheten som lot feilen stå i dagevis.
          tekst: `⚠ Køet i ${min} min — broen svarer ikke`, klasse: 'fail',
          detalj: 'Broen henter normalt hvert par sekund. Meldingen er IKKE tapt, men '
            + 'den blir liggende til broen er tilbake. Se Samband-diagnosen.',
        };
      }
      return {
        tekst: 'Køet for GNÅ', klasse: 'pending',
        detalj: 'Hubben har meldingen. Broen har ikke hentet den ennå.',
      };
    }
  }
}

/**
 * Hvor lenge vi venter før neste statusoppslag.
 *
 * ⚠ MÅ TRAPPE OPP. En melding som står i kø fordi broen er nede vil ellers
 * spørre hubben hvert sekund i timevis — per melding. Første sekundene er tette
 * (levering skjer normalt raskt), deretter roer det seg.
 */
export function nestePollMs(forsok: number): number {
  if (forsok <= 1) return 1500;
  if (forsok <= 3) return 3000;
  if (forsok <= 6) return 8000;
  if (forsok <= 12) return 20_000;
  return 60_000;
}

/**
 * Slutter vi å spørre?
 *
 * ⚠ VI GIR OPP Å SPØRRE, MEN VISER IKKE «LEVERT». En melding vi har sluttet å
 * følge står som den sist var kjent — «Køet i 42 min», ikke noe bedre.
 *
 * ⚠ TALLET ER SATT AV VARIGHETEN, IKKE OMVENDT. Med `nestePollMs` gir 50 oppslag
 * ~40 minutters oppfølging. Det var 40 oppslag (~29,5 min), og en test krevde
 * over en halvtime — med god grunn: en bro som redeployes eller et nettbrudd i
 * felt varer lett lenger enn en halvtime, og slutter vi å følge før det, står
 * meldingen som «Køet» for alltid uten at noen får vite mer.
 */
export const MAKS_POLL = 50;

/** Tolk hubbens `/samband/status`-svar til en status vi kan vise. */
export function lesStatusSvar(u: unknown): {
  status: SambandStatus; grunn: string; forsok: number; koetMs: number;
} {
  const o = (u ?? {}) as Record<string, unknown>;
  const raa = typeof o['status'] === 'string' ? o['status'] : '';
  const gyldig: SambandStatus[] = ['koet', 'utlevert', 'levert', 'oppgitt'];
  const status = (gyldig as string[]).includes(raa) ? (raa as SambandStatus) : 'ukjent';
  return {
    status,
    grunn: typeof o['grunn'] === 'string' ? o['grunn'] : '',
    forsok: typeof o['forsok'] === 'number' ? o['forsok'] : 0,
    koetMs: typeof o['koetMs'] === 'number' ? o['koetMs'] : 0,
  };
}
